// cspell:ignore Comlink, webworkers, bbox
import {useEffect, useRef, useCallback, useState} from 'react'
import * as Comlink from 'comlink'
import {getMockupWorker} from '../webworkers/helpers/customShape'
import {useCustomShapeCache} from './useCustomShapeCache'
import {createDebugger} from '../helpers/Debugger'

const debug = createDebugger({namespace: 'useCustomShapeWorker'})

/**
 * Converts an ImageBitmap to a PNG data URL via OffscreenCanvas.
 * @param {ImageBitmap} bitmap
 * @returns {Promise<string>}
 */
export async function bitmapToDataUrl(bitmap) {
    const canvas = new OffscreenCanvas(bitmap.width, bitmap.height)
    canvas.getContext('2d').drawImage(bitmap, 0, 0)
    const blob = await canvas.convertToBlob({type: 'image/png'})
    return new Promise((resolve) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result)
        reader.readAsDataURL(blob)
    })
}

const CANVAS_DIMENSION = {
    WIDTH: 1000,
    HEIGHT: 1000,
}

const EXPORT_DIR = 'Mockup-Custom_Shape_2-side_Printed_Wood_Bookshelf_Sign_1773905212'
export const ASSET_BASE_URL = `${EXPORT_DIR}/layers`

/**
 * Pure infrastructure hook — manages the Pixi worker lifecycle, cache, and update state.
 * Contains no business context; all business-level guards and context-binding live in
 * usePreviewCustomShape.
 *
 * @returns {{
 *   isReady: boolean,
 *   isUpdating: boolean,
 *   workerRef: React.RefObject,
 *   initWorker: (options: {canvas: HTMLCanvasElement|null, documentJson: object}) => Promise<object>,
 *   disposeWorker: () => void,
 *   resize: (options: {width: number, height: number}) => Promise<boolean>,
 *   loadDocument: (options: {documentJson: object, baseUrl?: string}) => Promise<boolean>,
 *   clearCache: () => void,
 *   getMockupImages: (sides: string[]) => Promise<(string|null)[]>,
 *   getArtworkImage: () => Promise<string|null>,
 *   setBackground: (imageSource: string|Blob) => Promise<boolean>,
 *   setCutSide: (imageSource: string|Blob, side?: 'front'|'back') => Promise<boolean>,
 *   setPrintSide: (imageSource: string|Blob, side: 'front'|'back') => Promise<boolean>,
 * }}
 */
export function useCustomShapeWorker() {
    const workerRef = useRef(null)
    const initPromiseRef = useRef(null)
    const [isReady, setIsReady] = useState(false)
    const [isUpdating, setIsUpdating] = useState(false)

    const {get: getCache, set: setCache, del: delCache, clear: clearCache} = useCustomShapeCache()

    // --- Worker lifecycle ---

    /**
     * Initializes the worker, sets up the canvas (or headless mode), and loads the document.
     * Idempotent — safe to call multiple times.
     * @param {{canvas: HTMLCanvasElement|null, documentJson: object}} options
     * @returns {Promise<object>} The worker API proxy
     */
    const initWorker = useCallback(async ({canvas, documentJson}) => {
        if (initPromiseRef.current) {
            debug.debug('initWorker: already initializing, reusing promise')
            return initPromiseRef.current
        }

        initPromiseRef.current = (async () => {
            debug.info('initWorker: starting')
            const api = await getMockupWorker()
            workerRef.current = api

            let width = CANVAS_DIMENSION.WIDTH
            let height = CANVAS_DIMENSION.HEIGHT

            if (canvas) {
                const container = canvas.parentElement
                const bbox = container?.getBoundingClientRect()
                if (bbox) {
                    width = Math.round(bbox.width || CANVAS_DIMENSION.WIDTH)
                    height = Math.round(bbox.height || CANVAS_DIMENSION.HEIGHT)
                }

                canvas.width = width
                canvas.height = height
                const offscreen = canvas.transferControlToOffscreen()
                await api.init(Comlink.transfer(offscreen, [offscreen]), width, height)
            } else {
                await api.init(null, width, height)
            }

            if (documentJson) {
                await api.loadDocument({documentJson, baseUrl: ASSET_BASE_URL})
            }

            setIsReady(true)
            debug.info('initWorker: ready', {width, height})
            return api
        })()

        return initPromiseRef.current
    }, [])

    /**
     * Disposes the worker, clears the init promise, and invalidates the cache.
     */
    const disposeWorker = useCallback(() => {
        if (workerRef.current) {
            debug.info('disposeWorker: disposing')
            workerRef.current.dispose()
            workerRef.current = null
        }
        initPromiseRef.current = null
        setIsReady(false)
        clearCache()
    }, [clearCache])

    useEffect(() => {
        return () => disposeWorker()
    }, [disposeWorker])

    // --- Worker API wrappers ---
    // All wrappers return null/false if the worker is not ready.

    /**
     * @param {{width: number, height: number}} options
     * @returns {Promise<boolean>}
     */
    const resize = useCallback((options) => workerRef.current?.resize(options) ?? false, [])

    /**
     * @param {boolean} [fitContain]
     * @returns {Promise<boolean>}
     */
    const render = useCallback((fitContain) => workerRef.current?.render(fitContain) ?? false, [])

    /**
     * @returns {Promise<string|null>}
     */
    const capture = useCallback(async () => {
        const bitmap = await workerRef.current?.capture()
        return bitmap ? bitmapToDataUrl(bitmap) : null
    }, [])

    /**
     * @param {'front'|'back'} side
     * @returns {Promise<boolean>}
     */
    const displaySide = useCallback((side) => workerRef.current?.displaySide(side) ?? false, [])

    /**
     * @param {{documentJson: object, baseUrl?: string}} options
     * @returns {Promise<boolean>}
     */
    const loadDocument = useCallback((options) => workerRef.current?.loadDocument(options) ?? false, [])

    /**
     * @param {string|Blob|null} imageSource
     * @returns {Promise<boolean>}
     */
    const updateBackground = useCallback((imageSource) => workerRef.current?.updateBackground(imageSource) ?? false, [])

    /**
     * @param {string|Blob|null} imageSource
     * @param {'front'|'back'} [side] - The side this cut-side image belongs to.
     * @returns {Promise<boolean>}
     */
    const updateCutSide = useCallback((imageSource, side) => workerRef.current?.updateCutSide(imageSource, side) ?? false, [])

    /**
     * @param {string|Blob|null} imageSource
     * @returns {Promise<boolean>}
     * */
    const updateFrontSide = useCallback((imageSource) => workerRef.current?.updateFrontSide(imageSource) ?? false, [])

    /**
     * @param {string|Blob|null} imageSource
     * @returns {Promise<boolean>}
     * */
    const updateBackSide = useCallback((imageSource) => workerRef.current?.updateBackSide(imageSource) ?? false, [])

    /**
     * @returns {Promise<string|null>}
     */
    const getArtworkDataUrl = useCallback(async () => {
        const bitmap = await workerRef.current?.getArtworkBitmap()
        return bitmap ? bitmapToDataUrl(bitmap) : null
    }, [])

    // --- High-level API (no business-logic guards — handled by callers) ---

    /**
     * Renders mockups for all given sides and returns an array of data URLs.
     * Wraps the entire batch in a single isUpdating cycle.
     * @param {string[]} sides
     * @returns {Promise<(string|null)[]>}
     */
    const getMockupImages = useCallback(
        async (sides) => {
            debug.debug('getMockupImages called with sides:', sides)
            if (!isReady || !workerRef.current || !sides?.length) return []

            // Return from cache if every side is cached
            const cachedResults = sides.map((side) => getCache(side))
            if (cachedResults.every(Boolean)) return cachedResults

            try {
                setIsUpdating(true)

                const results = []
                for (const side of sides) {
                    const cached = getCache(side)
                    if (cached) {
                        results.push(cached)
                        continue
                    }

                    await displaySide(side)
                    await render()
                    const dataUrl = await capture()
                    if (dataUrl) setCache(side, dataUrl)
                    results.push(dataUrl)
                }
                return results
            } catch (error) {
                debug.error('getMockupImages error:', error)
                return sides.map(() => null)
            } finally {
                setIsUpdating(false)
            }
        },
        [isReady, workerRef, getCache, setCache, displaySide, render, capture],
    )

    /**
     * Returns the current artwork layer as a data URL.
     * @returns {Promise<string|null>}
     */
    const getArtworkImage = useCallback(async () => {
        debug.debug('getArtworkImage called')
        if (!isReady) return null
        try {
            return await getArtworkDataUrl()
        } catch (err) {
            debug.error('getArtworkImage error:', err)
            return null
        }
    }, [isReady, getArtworkDataUrl])

    /**
     * @param {string|Blob} imageSource
     * @returns {Promise<boolean>}
     */
    const setBackground = useCallback(
        async (imageSource) => {
            debug.debug('setBackground called with imageSource:', imageSource)
            if (typeof imageSource !== 'string' && !(imageSource instanceof Blob)) return false
            try {
                await updateBackground(imageSource)
                await render()
                clearCache()
                return true
            } catch (err) {
                debug.error('setBackground error:', err)
                return false
            }
        },
        [updateBackground, render, clearCache],
    )

    /**
     * @param {string|Blob} imageSource
     * @param {'front'|'back'} [side='front'] - The side this cut-side image belongs to.
     * @returns {Promise<boolean>}
     */
    const setCutSide = useCallback(
        async (imageSource, side = 'front') => {
            debug.debug('setCutSide called with imageSource:', imageSource, 'side:', side)
            if (typeof imageSource !== 'string' && !(imageSource instanceof Blob)) return false
            try {
                setIsUpdating(true)
                await updateCutSide(imageSource, side)
                await render()
                clearCache()
                return true
            } catch (err) {
                debug.error('setCutSide error:', err)
                return false
            } finally {
                setIsUpdating(false)
            }
        },
        [updateCutSide, render, clearCache],
    )

    /**
     * @param {string|Blob} imageSource
     * @param {'front'|'back'} side
     * @returns {Promise<boolean>}
     */
    const setPrintSide = useCallback(
        async (imageSource, side) => {
            debug.debug('setPrintSide called with imageSource:', imageSource, 'side:', side)
            if (typeof imageSource !== 'string' && !(imageSource instanceof Blob)) return false
            try {
                setIsUpdating(true)
                if (side === 'front') await updateFrontSide(imageSource)
                else if (side === 'back') await updateBackSide(imageSource)
                await render()
                delCache(side)
                return true
            } catch (err) {
                debug.error('setPrintSide error:', err)
                return false
            } finally {
                setIsUpdating(false)
            }
        },
        [updateFrontSide, updateBackSide, render, delCache],
    )

    return {
        isReady,
        isUpdating,
        workerRef,
        initWorker,
        disposeWorker,
        resize,
        loadDocument,
        clearCache,
        getMockupImages,
        getArtworkImage,
        setBackground,
        setCutSide,
        setPrintSide,
    }
}

